import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiKeyLight, PiShieldCheckLight, PiUserLight } from 'react-icons/pi';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, FieldGrid } from '../../shared/brand/Field';
import { formatDateTime } from '../../../utils/functions';
import { formatRoleNameForDisplay } from '../../../utils/roleFormatter';
import sectionIcon from './sectionIcons';
import {
  AdminUser,
  TYPE_BADGE_COLOR,
  UserPermission,
  capitalizeSection,
  dedupeByLicenseId,
  permissionSectionNames,
} from './userEditModel';

export interface UserAccessPanelProps {
  user: AdminUser;
  /** `/admin/users/:id/licenses` payload, or null when it could not be read. */
  licenseSummary: any | null;
  permissions: UserPermission[];
  onManageLicense: () => void;
  onViewPermissions: () => void;
}

/** A chip listing one section a person's access covers. */
const SectionChip: React.FC<{ name: string; suffix?: React.ReactNode }> = ({ name, suffix }) => (
  <span className="inline-flex items-center gap-1 rounded border border-hairline-soft bg-surface-sunken/70 px-2 py-1 text-xs text-fg-body">
    {sectionIcon(name, 'h-3 w-3')}
    {capitalizeSection(name)}
    {suffix}
  </span>
);

/**
 * What this person is and what they may do - role, admin status, licence and
 * the areas their permissions cover, as three glass panes rather than the
 * three hairline-divided strips this used to be.
 */
const UserAccessPanel: React.FC<UserAccessPanelProps> = ({
  user,
  licenseSummary,
  permissions,
  onManageLicense,
  onViewPermissions,
}) => {
  const { t } = useTranslation();
  const basePlan = licenseSummary?.base_plan;
  const addons = dedupeByLicenseId<any>(licenseSummary?.user_active_addons);
  const sections = permissionSectionNames(permissions);

  return (
    <div className="space-y-4">
      <GlassCard icon={<PiUserLight />} title={t('Details')}>
        <FieldGrid columns={4}>
          <Field
            label={t('Role')}
            value={user.role ? formatRoleNameForDisplay(user.role) : ''}
          />
          <Field label={t('Admin')} value={user.is_org_admin ? t('Yes') : t('No')} />
          <Field label={t('Status')}>
            <Badge color={user.is_active ? 'green' : 'gray'}>
              {user.is_active ? t('Active') : t('Inactive')}
            </Badge>
          </Field>
          <Field label={t('Joined')} value={formatDateTime(user.created_at)} />
        </FieldGrid>
      </GlassCard>

      <GlassCard
        icon={<PiShieldCheckLight />}
        title={t('License')}
        actions={
          <Button variant="tertiary" size="small" onClick={onManageLicense}>
            {t('Manage')}
          </Button>
        }
      >
        {licenseSummary ? (
          <div className="space-y-4">
            <FieldGrid columns={4}>
              <Field label={t('Plan')}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="text-sm font-medium text-fg-body">
                    {basePlan?.display_name || basePlan?.name || '—'}
                  </p>
                  {basePlan?.type && (
                    <Badge color={TYPE_BADGE_COLOR[basePlan.type] || 'gray'}>
                      {basePlan.type.charAt(0).toUpperCase() + basePlan.type.slice(1)}
                    </Badge>
                  )}
                </div>
              </Field>
              <Field label={t('Seat')}>
                <Badge color={licenseSummary.has_base_seat ? 'green' : 'yellow'}>
                  {licenseSummary.has_base_seat ? t('Assigned') : t('No seat')}
                </Badge>
              </Field>
              <Field
                label={t('Price')}
                value={basePlan?.price_monthly != null ? `€${basePlan.price_monthly}/${t('mo')}` : ''}
              />
              <Field
                label={t('Expires')}
                value={basePlan?.license_expires_at ? formatDateTime(basePlan.license_expires_at) : ''}
              />
            </FieldGrid>

            {addons.length > 0 && (
              <div className="border-t border-hairline-soft pt-3">
                <p className="text-[11px] font-medium text-fg-muted">{t('Add-ons')}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {addons.map((addon: any) => (
                    <SectionChip
                      key={addon.license_id}
                      name={addon.display_name || addon.name}
                      suffix={
                        addon.price_monthly != null ? (
                          <span className="ms-1 text-fg-muted">€{addon.price_monthly}/{t('mo')}</span>
                        ) : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-fg-muted">{t('No license data available')}</p>
        )}
      </GlassCard>

      {sections.length > 0 && (
        <GlassCard
          icon={<PiKeyLight />}
          title={t('Permissions')}
          subtitle={t('{{count}} permission(s) granted', { count: permissions.length })}
          actions={
            <Button variant="tertiary" size="small" onClick={onViewPermissions}>
              {t('View all')}
            </Button>
          }
        >
          <div className="flex flex-wrap gap-1.5">
            {sections.map((name) => (
              <SectionChip key={name} name={name} />
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default UserAccessPanel;
